import React, { useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import { WelletContext } from '../../../context/wellet/welletContext';
import { getProfilePic } from '../../../services';
import { NoPhotoIcon } from '../../icons';
import { Show } from '../../../components';
import { AlertCircle, Building2, CheckCircle2, Handshake, Users } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';

const UserImageNew = ({
  url,
  name,
  size = '35px',
  className,
  affiliateType,
  verificationStatus = 'verified',
  verificationPercentage,
  showVerifiedIcon = true,
  showAffiliateIcon = true,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'generic' });
  const welletContext = useContext(WelletContext);
  const retryNumber = +process.env.REACT_APP_RETRY_COUNT;
  const retryDelay = +process.env.REACT_APP_RETRY_COUNT_DELAY;
  const profileQuery = useQuery({
    queryKey: ['profile-pic', url],
    queryFn: () => getProfilePic(welletContext, url.slice(url.indexOf('/users'))),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled: !!url,
    retry: retryNumber,
    retryDelay: retryDelay,
  });

  const affiliationColors = {
    CONCIERGE: {
      bg: 'tw-bg-white',
      border: 'tw-border-gray-200',
      icon: Building2,
      iconColor: 'tw-text-gray-700',
    },
    INDEPENDENT: {
      bg: 'tw-bg-white',
      border: 'tw-border-gray-200',
      icon: Users,
      iconColor: 'tw-text-gray-700',
    },
    INTERNAL_STAFF: {
      bg: 'tw-bg-white',
      border: 'tw-border-gray-200',
      icon: Handshake,
      iconColor: 'tw-text-gray-700',
    },
  };

  const verificationColors = {
    verified: {
      bg: 'tw-bg-green-100',
      border: 'tw-border-green-400',
      icon: CheckCircle2,
      iconColor: 'tw-text-green-600', // Added green-600 for dark contrast
    },
    partial: {
      bg: 'tw-bg-amber-100',
      border: 'tw-border-amber-400',
      icon: AlertCircle,
      iconColor: 'tw-text-amber-600', // Added amber-600 for dark contrast
    },
    unverified: {
      bg: 'tw-bg-gray-100',
      border: 'tw-border-gray-300',
      icon: AlertCircle,
      iconColor: 'tw-text-gray-500', // Added gray-500 for dark contrast
    },
  };

  const affiliation = affiliationColors[affiliateType];
  const verification = verificationColors[verificationStatus];
  const AffiliationIcon = affiliation.icon;
  const VerificationIcon = verification.icon;

  const imageUrl = useMemo(
    () =>
      profileQuery.isSuccess
        ? URL.createObjectURL(new Blob([profileQuery?.data?.data], { type: 'image/jpeg' }))
        : null,
    [url, profileQuery.isSuccess, profileQuery.data],
  );

  return (
    <>
      <div className='avatar-container' style={{ width: size }}>
        <Show>
          <Show.When isTrue={imageUrl}>
            <img
              className={className}
              style={{
                height: size,
                width: size,
                borderRadius: '50%',
                objectFit: 'cover',
              }}
              src={imageUrl}
              alt={`Foto de ${name}`}
            />
          </Show.When>
          <Show.Else>
            <div className='tw-inline-block'>
              <NoPhotoIcon size={size} className={className} />
            </div>
          </Show.Else>
        </Show>
        <Show.When isTrue={showAffiliateIcon}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className='tw-bg-transparent'>
                <div
                  className={`tw-absolute  tw--bottom-2 tw--right-2 ${affiliation.bg} tw-border-2 ${affiliation.border} tw-rounded-full tw-p-0.5`}
                  title={affiliation.label}
                >
                  <AffiliationIcon className={`tw-h-3.5 tw-w-3.5 ${affiliation.iconColor}`} />
                </div>
              </TooltipTrigger>
              <TooltipContent>{t(affiliateType.toLocaleLowerCase())}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Show.When>

        <Show.When isTrue={showVerifiedIcon}>
          {/* Verification badge - bottom right */}
          <div
            className={`tw-absolute tw--top-1 tw--right-2  ${verification.bg} tw-border-2 ${verification.border} tw-rounded-full tw-p-0.5`}
            title={
              verificationStatus === 'partial'
                ? `${verificationPercentage}% verificado`
                : verificationStatus === 'verified'
                ? 'Verificado'
                : 'No verificado'
            }
          >
            <VerificationIcon className={`tw-h-3.5 tw-w-3.5 ${verification.iconColor}`} />
          </div>
        </Show.When>
        {/* <Show.When isTrue={isVerified}>
          <div className='verified-avatar' style={verifiedStyle}>
            <VerifiedIcon size={verifiedSize} />
          </div>
        </Show.When> */}
      </div>
    </>
  );
};

export default UserImageNew;
